import type { AlertSeverity, CheckState, CheckStatus, MailEventType, OnboardState } from '@fkwd-qc/shared';

/**
 * The six visual tones the status dot and status pill can take. Every domain enum in the
 * app funnels through one of these so the palette stays closed.
 *
 * @since 1.0.0
 */
export type StatusTone = 'pass' | 'warn' | 'fail' | 'skip' | 'info' | 'off';

/**
 * Maps a check outcome onto a visual tone.
 *
 * @since 1.0.0
 * @param status - the check status
 * @returns the tone to render it in
 */
export function statusTone(status: CheckStatus): StatusTone {
    if (status === 'pass' || status === 'warn' || status === 'fail') {
        return status;
    }

    if (status === 'running') {
        return 'info';
    }

    return 'skip';
}

/**
 * Maps an alert severity onto a visual tone.
 *
 * @since 1.0.0
 * @param severity - the alert severity
 * @returns the tone to render it in
 */
export function severityTone(severity: AlertSeverity): StatusTone {
    if (severity === 'critical') {
        return 'fail';
    }

    if (severity === 'warning') {
        return 'warn';
    }

    return 'info';
}

/**
 * Four-character severity labels for the fixed-width column on the overview alert rows.
 *
 * @since 1.0.0
 */
export const SEVERITY_SHORT_LABELS: Record<AlertSeverity, string> = {
    critical: 'FAIL',
    warning: 'WARN',
    info: 'INFO',
};

/**
 * Maps an alert routing row onto a visual tone. Recovery is not a severity but is routed
 * alongside them on the settings view.
 *
 * @since 1.0.0
 * @param severity - the severity, or the literal `recovery`
 * @returns the tone to render it in
 */
export function routeSeverityTone(severity: AlertSeverity | 'recovery'): StatusTone {
    return severity === 'recovery' ? 'pass' : severityTone(severity);
}

/**
 * Maps a normalised mail event type onto a visual tone.
 *
 * @since 1.0.0
 * @param eventType - the event type
 * @returns the tone to render it in
 */
export function mailEventTone(eventType: MailEventType): StatusTone {
    if (eventType === 'delivered') {
        return 'pass';
    }

    if (eventType === 'accepted') {
        return 'info';
    }

    if (eventType === 'temporary_fail') {
        return 'warn';
    }

    return 'fail';
}

/**
 * Renders a normalised mail event type the way the log column does.
 *
 * @since 1.0.0
 * @param eventType - the event type
 * @returns the label with underscores replaced by spaces
 */
export function mailEventLabel(eventType: MailEventType): string {
    return eventType.replace(/_/g, ' ');
}

/**
 * Maps a check lifecycle state onto a visual tone.
 *
 * @since 1.0.0
 * @param state - the lifecycle state
 * @returns the tone to render it in
 */
export function checkStateTone(state: CheckState): StatusTone {
    if (state === 'live') {
        return 'pass';
    }

    if (state === 'baseline') {
        return 'info';
    }

    return 'off';
}

/**
 * Wording for each check lifecycle state.
 *
 * @since 1.0.0
 */
export const CHECK_STATE_LABELS: Record<CheckState, string> = {
    live: 'ready',
    baseline: 'needs data',
    planned: 'available',
};

/**
 * Maps a client onboarding state onto a visual tone.
 *
 * @since 1.0.0
 * @param state - the onboarding state
 * @returns the tone to render it in
 */
export function onboardTone(state: OnboardState): StatusTone {
    return state === 'live' ? 'pass' : 'warn';
}

/**
 * Wording for each client onboarding state.
 *
 * @since 1.0.0
 */
export const ONBOARD_LABELS: Record<OnboardState, string> = {
    live: 'complete',
    partial: 'setup incomplete',
    pending: 'not started',
};

/**
 * Resolves the display name of a check slug, falling back to the slug itself for one the
 * registry does not list, such as the synthetic alert storm.
 *
 * @since 1.3.0
 * @param slug - the check slug
 * @param names - the known slug-to-name lookup
 * @returns the display name, or the raw slug when it is unknown
 */
export function resolveCheckLabel(slug: string, names: ReadonlyMap<string, string>): string {
    return names.get(slug) ?? slug;
}
